type FlattenOpts = {
  maxDepth?: number;
  maxKeys?: number; // max total produced entries
  maxArray?: number; // max items per array
};

function isPlainObject(v: unknown): v is Record<string, any> {
  return !!v && typeof v === "object" && (v as any).constructor === Object;
}

export function flattenValueToPaths(
  value: unknown,
  opts: FlattenOpts = {},
): Record<string, unknown> {
  const { maxDepth = 8, maxKeys = 200, maxArray = 30 } = opts;

  const out: Record<string, unknown> = {};
  let produced = 0;

  const set = (path: string, v: unknown) => {
    if (produced >= maxKeys) return;
    out[path] = v;
    produced += 1;
  };

  const walk = (v: unknown, path: string, depth: number) => {
    if (produced >= maxKeys) return;

    // primitives + null/undefined
    if (v === null || v === undefined || typeof v !== "object") {
      set(path, v);
      return;
    }

    // File / Blob — залишаємо метадані
    if (typeof File !== "undefined" && v instanceof File) {
      set(path, { __type: "File", name: v.name, type: v.type, size: v.size });
      return;
    }
    if (typeof Blob !== "undefined" && v instanceof Blob) {
      set(path, { __type: "Blob", type: v.type, size: v.size });
      return;
    }

    if (depth >= maxDepth) {
      // не розкриваємо далі, просто маркер
      if (Array.isArray(v))
        set(path, { __truncated: true, __type: "array", len: v.length });
      else set(path, { __truncated: true, __type: "object" });
      return;
    }

    // arrays
    if (Array.isArray(v)) {
      if (v.length === 0) {
        set(path, []);
        return;
      }
      const limit = Math.min(v.length, maxArray);
      for (let i = 0; i < limit; i++) {
        walk(v[i], `${path}[${i}]`, depth + 1);
      }
      if (v.length > limit) {
        set(`${path}[*]`, { __truncated: true, len: v.length });
      }
      return;
    }

    // objects
    if (isPlainObject(v)) {
      const keys = Object.keys(v);
      if (keys.length === 0) {
        set(path, {});
        return;
      }
      for (const k of keys) {
        walk(v[k], `${path}.${k}`, depth + 1);
        if (produced >= maxKeys) break;
      }
      return;
    }

    // other object types (Date/Map/Set/etc)
    const ctor = (v as any)?.constructor?.name ?? "Object";
    if (v instanceof Date) set(path, v.toISOString());
    else set(path, { __type: ctor });
  };

  walk(value, "value", 0);
  return out;
}
